/**
 * HistoricalOddsWidget — View historical odds snapshots
 * Extracted from: fetchHistorical() lines 2579-2648
 * API: GET /historical/:sport (10x regions x markets tokens — EXPENSIVE)
 * CSS prefix: how-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchOddsSports, fetchOddsHistorical } from '../lib/api'
import { getSportIcon } from '../lib/oddsConstants'
import OddsTable from './shared/OddsTable'
import { BookOpen, RefreshCw, ChevronRight, AlertTriangle } from 'lucide-react'
import './HistoricalOddsWidget.css'

const REGIONS = [
  { id: 'us', label: 'US' },
  { id: 'uk', label: 'UK' },
  { id: 'eu', label: 'EU' },
  { id: 'au', label: 'AU' },
]

const MARKETS = [
  { id: 'h2h', label: 'Moneyline' },
  { id: 'spreads', label: 'Spreads' },
  { id: 'totals', label: 'Totals' },
]

function HistoricalOddsWidget({ onQuotaUpdate }) {
  const [sports, setSports] = useState([])
  const [selectedSport, setSelectedSport] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedRegions, setSelectedRegions] = useState(new Set(['us']))
  const [selectedMarkets, setSelectedMarkets] = useState(new Set(['h2h']))
  const [oddsFormat, setOddsFormat] = useState('american')
  const [histData, setHistData] = useState(null)
  const [snapshotInfo, setSnapshotInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sportsLoading, setSportsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [lastFetch, setLastFetch] = useState(null)

  useEffect(() => {
    async function loadSports() {
      setSportsLoading(true)
      const res = await fetchOddsSports()
      if (res.status === 'ok' && res.data) {
        setSports(res.data.filter(s => s.active))
      }
      setSportsLoading(false)
    }
    loadSports()

    // Default date to today
    const now = new Date()
    setSelectedDate(now.toISOString().slice(0, 16))
  }, [])

  const toggleRegion = (id) => {
    setSelectedRegions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleMarket = (id) => {
    setSelectedMarkets(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const tokenCost = selectedRegions.size * selectedMarkets.size * 10

  const fetchData = useCallback(async () => {
    if (!selectedSport || !selectedDate) return
    if (selectedRegions.size === 0 || selectedMarkets.size === 0) return

    setLoading(true)
    setError(null)

    const params = {
      regions: Array.from(selectedRegions).join(','),
      markets: Array.from(selectedMarkets).join(','),
      date: new Date(selectedDate).toISOString(),
      oddsFormat: oddsFormat,
    }

    const res = await fetchOddsHistorical(selectedSport, params)

    if (res.status === 'ok' && res.data) {
      setSnapshotInfo({
        timestamp: res.data.timestamp,
        previous: res.data.previous_timestamp,
        next: res.data.next_timestamp,
      })
      setHistData(res.data.data || [])
      setLastFetch(new Date())
      if (onQuotaUpdate && (res.remaining || res.used)) onQuotaUpdate(res.remaining, res.used)
    } else {
      setError(res.message || 'Failed to load historical data')
      setHistData(null)
      setSnapshotInfo(null)
    }

    setLoading(false)
  }, [selectedSport, selectedDate, selectedRegions, selectedMarkets, oddsFormat])

  return (
    <div className="widget-card">
      <div
        className="widget-header how-header-clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <BookOpen size={16} className="widget-icon" />
        <span className="widget-title">Historical Odds</span>
        <span className="status-badge error" style={{ fontSize: 9 }}>
          {tokenCost} TOKENS (10x)
        </span>
        <ChevronRight
          size={16}
          className={`how-chevron ${expanded ? 'rotated' : ''}`}
        />
      </div>

      {expanded && (
        <>
          {/* Cost warning */}
          <div className="how-warning">
            <AlertTriangle size={14} />
            <span>Historical data costs <strong>10x normal rate</strong>. Each request uses {tokenCost} tokens.</span>
          </div>

          <div className="how-controls">
            <div className="how-row">
              <select
                className="how-select"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                disabled={sportsLoading}
              >
                <option value="">{sportsLoading ? 'Loading...' : 'Select sport'}</option>
                {sports.map(s => (
                  <option key={s.key} value={s.key}>
                    {getSportIcon(s.key)} {s.title}
                  </option>
                ))}
              </select>

              <input
                type="datetime-local"
                className="how-date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />

              <div className="how-format-toggle">
                <button
                  className={`how-format-btn ${oddsFormat === 'american' ? 'active' : ''}`}
                  onClick={() => setOddsFormat('american')}
                >
                  American
                </button>
                <button
                  className={`how-format-btn ${oddsFormat === 'decimal' ? 'active' : ''}`}
                  onClick={() => setOddsFormat('decimal')}
                >
                  Decimal
                </button>
              </div>
            </div>

            <div className="how-row">
              <div className="how-checkbox-group">
                <span className="how-group-label">Regions:</span>
                {REGIONS.map(r => (
                  <label key={r.id} className="how-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRegions.has(r.id)}
                      onChange={() => toggleRegion(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>

              <div className="how-checkbox-group">
                <span className="how-group-label">Markets:</span>
                {MARKETS.map(m => (
                  <label key={m.id} className="how-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedMarkets.has(m.id)}
                      onChange={() => toggleMarket(m.id)}
                    />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>

              <button
                className="how-fetch-btn"
                onClick={fetchData}
                disabled={!selectedSport || !selectedDate || selectedRegions.size === 0 || selectedMarkets.size === 0 || loading}
              >
                {loading ? <RefreshCw size={13} className="spinning" /> : <BookOpen size={13} />}
                <span>Get Historical</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="widget-body">
              <div className="widget-error">{error}</div>
            </div>
          )}

          {loading && (
            <div className="widget-body">
              <div className="widget-empty">Loading historical data...</div>
            </div>
          )}

          {/* Snapshot info */}
          {snapshotInfo && (
            <div className="how-snapshot">
              <div className="how-snap-item">
                <span className="how-snap-label">Snapshot:</span>
                <span className="how-snap-value">{snapshotInfo.timestamp || '-'}</span>
              </div>
              <div className="how-snap-item">
                <span className="how-snap-label">Previous:</span>
                <span className="how-snap-value">{snapshotInfo.previous || 'N/A'}</span>
              </div>
              <div className="how-snap-item">
                <span className="how-snap-label">Next:</span>
                <span className="how-snap-value">{snapshotInfo.next || 'N/A'}</span>
              </div>
            </div>
          )}

          {!loading && histData && histData.length > 0 && (
            <OddsTable games={histData} oddsFormat={oddsFormat} />
          )}

          {!loading && histData && histData.length === 0 && (
            <div className="widget-body">
              <div className="widget-empty">No odds data in this historical snapshot.</div>
            </div>
          )}

          {!loading && !histData && !error && (
            <div className="widget-body">
              <div className="widget-empty">
                Select a sport and date to view historical odds snapshots.
              </div>
            </div>
          )}

          <div className="widget-footer">
            <span>{lastFetch ? `Updated ${lastFetch.toLocaleTimeString()}` : 'Not loaded'}</span>
            <span>Cost: {tokenCost} tokens per request</span>
          </div>
        </>
      )}
    </div>
  )
}

export default HistoricalOddsWidget
